/**
 * EingangMailView - Teil 1's mail, rendered as a mail (8.6).
 *
 * Sender with the Kanal, recipients, body. Voice follows MailReadingPane: the Eingang
 * here and the mail in Kommunikation are the same message, and a broker moves between
 * the two in one session.
 *
 * HTML goes through sanitizeEmailHtml - inbound mail is hostile input, never injected raw.
 * The Betreff is not repeated here; the Maske header carries it, and 8.6's list of
 * Eingangsmetadaten (Kanal, Absender, Zeitpunkt) does not include it.
 */
import { FileText, Globe, Mail, Upload } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { POSTEINGANG_KANAL_LABELS, type PosteingangKanal } from '@brokly/shared';
import type {
  CommunicationView,
  CommVisibilityTier,
  GenesisFact,
  GenesisOutcome,
} from '@brokly/shared';
import { Badge } from '../../../components/ui/Badge';
import { sanitizeEmailHtml } from '../../../lib/sanitize-email-html';
import { dateTimeShortDe } from '../../../lib/format';

const KANAL_ICON: Record<PosteingangKanal, ComponentType<{ className?: string }>> = {
  email: Mail,
  scanning_center: FileText,
  upload: Upload,
  portal: Globe,
};

/**
 * One genesis fact, in plain German words. NEVER a percentage or a score.
 * `register`/`reply_to`/`none` are the whole union; a null uid or seat is
 * omitted gracefully (no stray commas) rather than rendered as an empty segment.
 */
function genesisFactText(fact: GenesisFact): string {
  switch (fact.kind) {
    case 'register': {
      const details = [fact.name, fact.uid, fact.seat].filter((v): v is string => Boolean(v));
      return `Im Handelsregister: ${details.join(', ')}`;
    }
    case 'reply_to':
      return `Antwort auf unser Mail vom ${dateTimeShortDe(fact.sent_at)}`;
    case 'none':
      return 'Keine Registerdaten gefunden';
  }
}

/**
 * The Eingang's Kanal for the Teil-1 header. The SAME collapse the Posteingang list
 * applies server-side in POSTEINGANG_KANAL_SQL, so the list and the Maske never label
 * one Eingang differently.
 */
function kanalOf(channel: string | null | undefined): PosteingangKanal {
  if (channel === 'upload') return 'upload';
  if (channel === 'scanning_center' || channel === 'post') return 'scanning_center';
  if (channel === 'portal') return 'portal';
  return 'email';
}

/** An `An:`/`Cc:` line, rendered only when the wire actually carried one. */
function AddressLine({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-1.5">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-foreground/80">{value}</span>
    </div>
  );
}

export function EingangMailView({
  comm,
  tier,
  bodySlot,
  genesis,
  unconfirmedName,
}: {
  comm: CommunicationView;
  /** The caller's visibility tier for this mail; below full the empty body is a permission, not a gap. */
  tier?: CommVisibilityTier | null;
  /**
   * Rendered WHERE THE BODY WOULD GO, instead of it. The Bearbeitungsmaske puts the
   * Freigabe-Anfrage panel here (D2): it already says the content is locked, so the
   * placeholder below would only repeat it in a second dashed box.
   */
  bodySlot?: ReactNode;
  /**
   * The genesis intake decision (GET /communications/:id/content), sent at tier 'full'
   * only. Absent below that: render NOTHING, never a placeholder implying missing data.
   */
  genesis?: { outcome: GenesisOutcome; facts: GenesisFact[] } | null;
  /** The resolved display name, when this Eingang's customer is an unconfirmed record. */
  unconfirmedName?: string | null;
}) {
  const kanal = kanalOf(comm.channel);
  const KanalIcon = KANAL_ICON[kanal];

  return (
    <article className="flex flex-col gap-3">
      {/* ── Absender + Kanal + Zeitpunkt (8.6's three Eingangsmetadaten) ── */}
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground">
          <KanalIcon className="size-4" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 truncate text-sm font-medium text-foreground">
              {comm.from ??
                (kanal === 'upload'
                  ? 'Hochgeladenes Dokument'
                  : kanal === 'scanning_center'
                    ? 'Scan-Center'
                    : kanal === 'portal'
                      ? 'Kundenportal'
                      : 'Unbekannter Absender')}
            </span>
            <Badge variant="neutral">{POSTEINGANG_KANAL_LABELS[kanal]}</Badge>
          </div>
          <span className="text-xs text-muted-foreground">
            {dateTimeShortDe(comm.received_at)}
          </span>
          {/* The unconfirmed record's name plus the genesis facts, in plain German words, never a
              percentage or a score. Absent below tier 'full'. */}
          {unconfirmedName && (
            <div className="flex items-center gap-1.5">
              <Badge variant="neutral">Unbestätigt</Badge>
              <span className="text-sm text-foreground">{unconfirmedName}</span>
            </div>
          )}
          {genesis && genesis.facts.length > 0 && (
            <div className="flex flex-col gap-0.5">
              {genesis.facts.map((fact, i) => (
                <span key={i} className="text-xs text-muted-foreground">
                  {genesisFactText(fact)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {(comm.to || comm.cc) && (
        <div className="flex flex-col gap-0.5 text-xs">
          <AddressLine label="An:" value={comm.to} />
          <AddressLine label="Cc:" value={comm.cc} />
        </div>
      )}

      {/* ── Der Text ──
          An HTML mail renders as HTML (sanitized); a text mail keeps its line breaks.
          Either way it sits on its own surface so the body reads as the message and not
          as more page chrome. */}
      {bodySlot ?? (comm.body_html ? (
        <div
          data-testid="eingang-body-html"
          className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-sm leading-relaxed text-foreground/90 [&_a]:text-primary [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(comm.body_html) }}
        />
      ) : comm.body ? (
        <p className="whitespace-pre-line rounded-md border bg-muted/40 p-3 text-sm leading-relaxed text-foreground/90">
          {comm.body}
        </p>
      ) : (
        // A reduced-tier viewer gets the subject and no body: say that it is a permission.
        // A full-tier viewer of a mail without text is told that instead.
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          {tier && tier !== 'full' ? 'Inhalt nur für Beteiligte sichtbar.' : 'Kein Text vorhanden.'}
        </p>
      ))}
    </article>
  );
}
